/**
 * Audio Engine (spec section 18).
 *
 * Owns decoding, the effect chain, monitoring and - critically - the
 * MediaStreamAudioDestinationNode whose stream is muxed with the canvas
 * stream during export. No cloud audio processing anywhere.
 */

import { buildEffectGraph } from './effects.js';
import { readLiveFeatures, sampleFeatures, makeSilentAnalysis, analyzeAudioBuffer } from './analyser.js';
import { createLogger } from '../utils/log.js';

const log = createLogger('audio');

export const SUPPORTED_AUDIO_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/x-wav',
  'audio/wave',
  'audio/mp4',
  'audio/m4a',
  'audio/x-m4a',
  'audio/aac',
  'audio/ogg',
  'audio/webm',
  'audio/flac',
];

export const SUPPORTED_AUDIO_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.oga', '.opus', '.flac', '.webm'];

export function isLikelyAudioFile(file) {
  if (!file) return false;
  const type = String(file.type ?? '').toLowerCase();
  if (type.startsWith('audio/')) return true;
  const name = String(file.name ?? '').toLowerCase();
  return SUPPORTED_AUDIO_EXTENSIONS.some((ext) => name.endsWith(ext));
}

/**
 * Decode a file to an AudioBuffer.
 *
 * Uses an OfflineAudioContext so it works before any user gesture -
 * a regular AudioContext can be suspended at that point, which would
 * make the very first upload fail on mobile.
 */
export async function decodeAudioFile(file) {
  const arrayBuffer = await file.arrayBuffer();
  const Ctx = globalThis.OfflineAudioContext ?? globalThis.webkitOfflineAudioContext;
  if (!Ctx) throw new Error('Web Audio is not available in this browser.');
  const ctx = new Ctx(1, 1, 44100);
  const buffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
  return buffer;
}

export function createAudioEngine() {
  let ctx = null;
  let buffer = null;
  let effectGraph = null;
  let source = null;
  let destination = null; // MediaStreamAudioDestinationNode
  let monitorGain = null;
  let analyser = null;
  let analyserBuffer = null;
  let analysis = makeSilentAnalysis(30, 0);
  let startedAt = 0;
  let offsetAt = 0;
  let playing = false;
  let settings = { effectId: 'none', strength: 0.35, monitor: false, audioReactive: false };

  function ensureContext() {
    if (ctx) return ctx;
    const Ctx = globalThis.AudioContext ?? globalThis.webkitAudioContext;
    if (!Ctx) throw new Error('Web Audio is not available in this browser.');
    ctx = new Ctx({ latencyHint: 'interactive' });
    destination = ctx.createMediaStreamDestination();
    monitorGain = ctx.createGain();
    monitorGain.gain.value = settings.monitor ? 1 : 0;
    monitorGain.connect(ctx.destination);
    analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.75;
    analyserBuffer = new Uint8Array(analyser.frequencyBinCount);
    analyser.connect(destination);
    log.info('audio context created', { sampleRate: ctx.sampleRate });
    return ctx;
  }

  function rebuildGraph() {
    ensureContext();
    if (effectGraph) {
      effectGraph.dispose();
      effectGraph = null;
    }
    effectGraph = buildEffectGraph(ctx, settings.effectId, { strength: settings.strength });
    effectGraph.output.connect(analyser);
    effectGraph.output.connect(monitorGain);
    return effectGraph;
  }

  function detachSource() {
    if (source) {
      try {
        source.onended = null;
        source.stop();
      } catch {
        /* already stopped */
      }
      try {
        source.disconnect();
      } catch {
        /* already disconnected */
      }
      source = null;
    }
    playing = false;
  }

  const api = {
    get context() {
      return ctx;
    },
    get duration() {
      return buffer?.duration ?? 0;
    },
    get buffer() {
      return buffer;
    },
    get analysis() {
      return analysis;
    },
    get isPlaying() {
      return playing;
    },

    async resume() {
      ensureContext();
      if (ctx.state === 'suspended') await ctx.resume();
      return ctx.state;
    },

    async load(file) {
      const decoded = await decodeAudioFile(file);
      buffer = decoded;
      analysis = analyzeAudioBuffer(decoded, { fps: 30 });
      log.info('audio decoded', { duration: decoded.duration, channels: decoded.numberOfChannels });
      return {
        buffer: decoded,
        duration: decoded.duration,
        sampleRate: decoded.sampleRate,
        channels: decoded.numberOfChannels,
      };
    },

    /** Analyse an already-decoded buffer (used after restore). */
    analyse(fps = 30) {
      analysis = buffer ? analyzeAudioBuffer(buffer, { fps }) : makeSilentAnalysis(fps, 0);
      return analysis;
    },

    setEffect(effectId, strength) {
      settings = { ...settings, effectId, strength };
      rebuildGraph();
    },

    setMonitor(enabled) {
      settings = { ...settings, monitor: Boolean(enabled) };
      ensureContext();
      monitorGain.gain.value = settings.monitor ? 1 : 0;
    },

    prepare() {
      rebuildGraph();
      return api;
    },

    /** The audio stream that gets muxed into the export. */
    getRecordStream() {
      ensureContext();
      if (!destination) throw new Error('Audio destination is not ready.');
      return destination.stream;
    },

    /** Play from the start (or an offset) through the effect chain. */
    async play({ offset = 0, monitor = null } = {}) {
      ensureContext();
      if (!buffer) throw new Error('No audio loaded.');
      if (monitor !== null) api.setMonitor(monitor);
      await api.resume();
      detachSource();
      rebuildGraph();
      source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(effectGraph.input);
      offsetAt = offset;
      startedAt = ctx.currentTime;
      source.start(0, Math.max(0, offset));
      playing = true;
      source.onended = () => {
        playing = false;
      };
      return api;
    },

    stop() {
      detachSource();
      offsetAt = 0;
      startedAt = 0;
    },

    get currentTime() {
      if (!playing) return offsetAt;
      return offsetAt + (ctx.currentTime - startedAt);
    },

    /** Live features for the on-screen preview (not used for export). */
    liveFeatures() {
      if (!analyser) return { level: 0, bass: 0, mid: 0, treble: 0, beat: 0 };
      return readLiveFeatures(analyser, analyserBuffer);
    },

    /** Deterministic features at a timeline position (used for export). */
    featuresAt(seconds) {
      return sampleFeatures(analysis, seconds);
    },

    dispose() {
      detachSource();
      if (effectGraph) effectGraph.dispose();
      effectGraph = null;
      try {
        ctx?.close();
      } catch {
        /* ignore */
      }
      ctx = null;
      destination = null;
      analyser = null;
      monitorGain = null;
      buffer = null;
      analysis = makeSilentAnalysis(30, 0);
    },
  };

  return api;
}

/** Peak level of a decoded buffer, for the waveform strip in the UI. */
export function computeWaveform(buffer, buckets = 96) {
  if (!buffer) return new Float32Array(buckets);
  const data = buffer.getChannelData(0);
  const perBucket = Math.max(1, Math.floor(data.length / buckets));
  const out = new Float32Array(buckets);
  for (let b = 0; b < buckets; b += 1) {
    let peak = 0;
    const start = b * perBucket;
    const end = Math.min(data.length, start + perBucket);
    for (let i = start; i < end; i += 1) peak = Math.max(peak, Math.abs(data[i]));
    out[b] = Math.min(1, peak);
  }
  return out;
}
